import { Link } from "react-router-dom";
import { 
  ArrowLeft, 
  ArrowRight,
  CheckCircle, 
  DollarSign, 
  Mail, 
  MousePointer, 
  UserPlus, 
  Wallet,
  Clock,
  Shield,
  Star,
  Gift
} from "lucide-react";
import { Button } from "../components/ui/button";
import Footer from "../components/Footer";

export default function HowToEarnPage() {
  const steps = [
    {
      number: "01",
      icon: UserPlus,
      title: "Create Your Free Account",
      description: "Sign up in under 2 minutes. All you need is an email address and a social media profile for verification. No fees, no hidden costs.",
      tips: [
        "Use a valid email you check regularly",
        "Add your social profile for faster approval",
        "Complete your profile to unlock more tasks"
      ]
    },
    {
      number: "02",
      icon: Mail,
      title: "Register Your Email Addresses",
      description: "Add up to 10 email addresses where you want to receive tasks. More emails means more earning opportunities!",
      tips: [
        "Use Gmail addresses for best deliverability",
        "Verify each email to start receiving tasks",
        "Check spam folders for verification emails"
      ]
    },
    {
      number: "03",
      icon: MousePointer,
      title: "Complete Simple Tasks",
      description: "When you receive a task email, simply click the link or follow the instructions. Most tasks take less than 30 seconds!",
      tips: [
        "Click tasks as soon as you receive them",
        "Read instructions carefully",
        "Some tasks auto-complete when you click"
      ]
    },
    {
      number: "04",
      icon: DollarSign,
      title: "Earn Points & Get Paid",
      description: "Every completed task earns you points. Once you reach the minimum threshold, withdraw to PayPal instantly!",
      tips: [
        "100 points = $1.00 USD",
        "Minimum withdrawal is $7.00",
        "Payments processed within 1-3 business days"
      ]
    }
  ];

  const taskTypes = [
    {
      icon: MousePointer,
      title: "Click Tasks",
      description: "Simply click a link in your email. Auto-completes instantly!",
      points: "10-50 points",
      time: "5 seconds"
    },
    {
      icon: Mail,
      title: "Email Reply Tasks",
      description: "Reply to an email with a simple message or confirmation.",
      points: "25-100 points",
      time: "1-2 minutes"
    },
    {
      icon: Star,
      title: "Survey Tasks",
      description: "Complete short surveys or feedback forms.",
      points: "50-200 points",
      time: "3-5 minutes"
    },
    {
      icon: Gift,
      title: "Bonus Tasks",
      description: "Special high-reward tasks for active members.",
      points: "100-500 points",
      time: "Varies"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#00B894] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
          <Link to="/register">
            <Button className="bg-[#00B894] hover:bg-[#00A085] text-white">
              Start Earning Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-white via-[#00B894]/5 to-[#0984E3]/5">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#00B894]/10 text-[#00B894] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Gift className="w-4 h-4" />
            Start earning today - No experience required
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            How to Earn Money with{" "}
            <span className="bg-gradient-to-r from-[#00B894] to-[#0984E3] bg-clip-text text-transparent">
              GigsEasy
            </span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed mb-8">
            Turn your spare time into real money. Complete simple email tasks from your phone or computer 
            and get paid via PayPal. It's that easy!
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <div className="flex items-center gap-2 text-slate-600">
              <CheckCircle className="w-5 h-5 text-[#00B894]" />
              <span>Free to join</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <CheckCircle className="w-5 h-5 text-[#00B894]" />
              <span>No experience needed</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <CheckCircle className="w-5 h-5 text-[#00B894]" />
              <span>Work from anywhere</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <CheckCircle className="w-5 h-5 text-[#00B894]" />
              <span>PayPal payments</span>
            </div>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              4 Simple Steps to Start Earning
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Getting started takes less than 5 minutes. Follow these steps and you could be earning within the hour!
            </p>
          </div>

          <div className="space-y-12">
            {steps.map((step, index) => (
              <div 
                key={index}
                className={`flex flex-col lg:flex-row gap-8 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-5xl font-bold text-[#00B894]/20">{step.number}</span>
                    <div className="w-14 h-14 bg-gradient-to-br from-[#00B894] to-[#0984E3] rounded-2xl flex items-center justify-center">
                      <step.icon className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-slate-600 mb-4 text-lg">{step.description}</p>
                  <ul className="space-y-2">
                    {step.tips.map((tip, tipIndex) => (
                      <li key={tipIndex} className="flex items-start gap-2 text-slate-600">
                        <CheckCircle className="w-5 h-5 text-[#00B894] flex-shrink-0 mt-0.5" />
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex-1 bg-slate-50 rounded-2xl p-8 w-full">
                  <div className="aspect-video bg-gradient-to-br from-[#00B894]/10 to-[#0984E3]/10 rounded-xl flex items-center justify-center">
                    <step.icon className="w-20 h-20 text-[#00B894]/40" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Task Types Section */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Types of Tasks You'll Complete
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              All our tasks are simple and straightforward. Here's what you can expect:
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {taskTypes.map((task, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-[#00B894]/10 rounded-xl flex items-center justify-center mb-4">
                  <task.icon className="w-6 h-6 text-[#00B894]" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{task.title}</h3>
                <p className="text-slate-600 text-sm mb-4">{task.description}</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#00B894] font-semibold">{task.points}</span>
                  <span className="text-slate-500 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {task.time}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Earnings Calculator */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-[#00B894] to-[#0984E3] rounded-3xl p-8 md:p-12 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Your Potential Earnings</h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              The more tasks you complete, the more you earn. Here's what active members typically make:
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 rounded-2xl p-6">
                <p className="text-3xl font-bold mb-2">$50-100</p>
                <p className="text-white/70 text-sm">Casual (1 hour/day)</p>
              </div>
              <div className="bg-white/20 rounded-2xl p-6 transform scale-105">
                <p className="text-4xl font-bold mb-2">$200-400</p>
                <p className="text-white/70 text-sm">Active (2-3 hours/day)</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-6">
                <p className="text-3xl font-bold mb-2">$500+</p>
                <p className="text-white/70 text-sm">Power User (Full-time)</p>
              </div>
            </div>
            <p className="text-white/60 text-sm">
              *Earnings vary based on task availability and completion rate
            </p>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#00B894]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#00B894]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">100% Legitimate</h3>
              <p className="text-slate-600">
                GigsEasy is a real company with real payments. We've paid out millions to our members worldwide.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#00B894]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Wallet className="w-8 h-8 text-[#00B894]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Fast Payments</h3>
              <p className="text-slate-600">
                Withdraw your earnings anytime once you hit the minimum. Payments processed within 1-3 business days.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#00B894]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-[#00B894]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Work Anytime</h3>
              <p className="text-slate-600">
                Complete tasks whenever it's convenient for you. No schedules, no deadlines, no pressure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Ready to Start Earning?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Join thousands of people who are already earning with GigsEasy. 
            It's free to sign up and takes less than 2 minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-[#00B894] hover:bg-[#00A085] text-white px-8">
                Create Free Account
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/faq">
              <Button size="lg" variant="outline" className="border-slate-300">
                Read FAQ First
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
